import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { baseUrl } from "../config";
import { useAuth } from "../AuthContext";
import { hasContributorAccess } from "../authRoles";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Chip,
  Stack,
} from "@mui/material";

const roleLabel = (name) => {
  switch (name) {
    case "admin":
      return "Administrateur";
    case "user":
      return "Contributeur";
    case "guest":
      return "Invité";
    case "advanced_contributor":
      return "Contributeur avancé";
    default:
      return name;
  }
};

const UserProfile = () => {
  const navigate = useNavigate();
  const { isLoggedIn, loading: authLoading, logout, setPseudo, setRoles } = useAuth();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [pseudo, setPseudoLocal] = useState("");
  const [email, setEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");

  useEffect(() => {
    if (authLoading) return;
    if (!isLoggedIn) {
      navigate("/login", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(baseUrl + "/me/profile", {
          withCredentials: true,
        });
        if (cancelled) return;
        setProfile(res.data);
        setPseudoLocal(res.data.pseudo || "");
        setEmail(res.data.email || "");
      } catch (e) {
        if (!cancelled) setError("Impossible de charger le profil.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isLoggedIn, navigate]);

  const refreshProfile = async () => {
    const res = await axios.get(baseUrl + "/me/profile", { withCredentials: true });
    setProfile(res.data);
    setPseudoLocal(res.data.pseudo || "");
    setEmail(res.data.email || "");
    setPseudo(res.data.pseudo);
    setRoles(Array.isArray(res.data?.roles) ? res.data.roles : []);
  };

  const handleSaveIdentity = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      await axios.put(
        baseUrl + "/me/profile",
        { pseudo: pseudo.trim(), email: email.trim() },
        { withCredentials: true }
      );
      setMessage("Profil mis à jour.");
      await refreshProfile();
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Impossible d'enregistrer les modifications.";
      setError(msg);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      await axios.put(
        baseUrl + "/me/password",
        { currentPassword, newPassword },
        { withCredentials: true }
      );
      setMessage("Mot de passe mis à jour.");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Impossible de changer le mot de passe.";
      setError(msg);
    }
  };

  const handleClaimRole = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      const res = await axios.post(
        baseUrl + "/me/claim-contributor-role",
        { answer: captchaAnswer },
        { withCredentials: true }
      );
      if (res.data?.already) {
        setMessage("Tu as déjà le rôle contributeur.");
      } else {
        setMessage("Rôle contributeur attribué.");
      }
      setCaptchaAnswer("");
      await refreshProfile();
    } catch (err) {
      const msg =
        err.response?.data?.error || "Réponse incorrecte.";
      setError(msg);
    }
  };

  const handleDeleteAccount = async () => {
    setError(null);
    try {
      await axios.delete(baseUrl + "/me", {
        data: { password: deletePassword },
        withCredentials: true,
      });
      logout();
      setDeleteOpen(false);
      navigate("/home", { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        "Suppression impossible (vérifie le mot de passe).";
      setError(msg);
    }
  };

  if (authLoading || loading) {
    return (
      <PageWrap>
        <Typography>Chargement…</Typography>
      </PageWrap>
    );
  }

  if (!profile) {
    return (
      <PageWrap>
        <Typography color="error">
          {error || "Profil indisponible."}
        </Typography>
      </PageWrap>
    );
  }

  const showCaptcha = !hasContributorAccess(profile.roles);

  return (
    <PageWrap>
      <Typography variant="h4" component="h1" gutterBottom>
        Mon profil
      </Typography>

      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Rôles
        </Typography>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {profile.roles.map((r) => (
            <Chip key={r.id} label={roleLabel(r.name)} size="small" />
          ))}
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleSaveIdentity}>
        <Typography variant="h6" gutterBottom>
          Pseudo et e-mail
        </Typography>
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <TextField
            label="Pseudo"
            value={pseudo}
            onChange={(e) => setPseudoLocal(e.target.value)}
            required
            fullWidth
            inputProps={{ maxLength: 128 }}
          />
          <TextField
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />
          <Button type="submit" variant="contained" color="primary">
            Enregistrer
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleChangePassword}>
        <Typography variant="h6" gutterBottom>
          Mot de passe
        </Typography>
        <Stack spacing={2} sx={{ maxWidth: 480 }}>
          <TextField
            label="Mot de passe actuel"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            fullWidth
          />
          <TextField
            label="Nouveau mot de passe"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            fullWidth
          />
          <Button type="submit" variant="outlined">
            Changer le mot de passe
          </Button>
        </Stack>
      </Paper>

      {showCaptcha && (
        <Paper sx={{ p: 2, mb: 2 }} component="form" onSubmit={handleClaimRole}>
          <Typography variant="h6" gutterBottom>
            Devenir contributeur
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Donner le nom d&apos;un des empereurs de Minefield.fr (réponse
            vérifiée automatiquement) pour obtenir le droit de créer des dates
            et voter.
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 480 }}>
            <TextField
              label="Réponse"
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              fullWidth
            />
            <Button type="submit" variant="contained" color="secondary">
              Valider
            </Button>
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom color="error">
          Zone sensible
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          La suppression du compte retire tes votes et détache tes contenus
          (les dates, périodes et frises restent sur le site sans auteur lié).
        </Typography>
        <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>
          Supprimer mon compte
        </Button>
      </Paper>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Supprimer le compte</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette action est définitive. Entre ton mot de passe pour confirmer.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Mot de passe"
            type="password"
            fullWidth
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            autoComplete="current-password"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Annuler</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAccount}
            disabled={!deletePassword}
          >
            Supprimer définitivement
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrap>
  );
};

const PageWrap = styled(Box)`
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 48px;
`;

export default UserProfile;
